import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Camera, Eye, EyeOff, Plus, Repeat, Settings2, Shield, Trash2, UserPlus, X } from 'lucide-react';
import { HIDABLE_MODULES, ROLE_LABELS, WORKSPACE_TYPE_LABELS, type HidableModule, type Workspace, type WorkspaceMember } from '@unitly/contracts';
import {
  useDeleteWorkspace, useDeleteWorkspaceLogo, useMembers, useSections, useSetModuleVisibility, useTransferWorkspace,
  useUpdateWorkspaceMemberSettings, useUploadWorkspaceLogo, useWorkspace, useWorkspaceMember, useWorkspaces,
} from '../api/queries.ts';
import { useOrgCtx } from '../app/org-context.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { PageHeader } from '../features/shell/Chrome.tsx';
import { TaskExplorer } from './Tasks.tsx';
import { PedView } from '../features/editorial/PedView.tsx';
import { PagesView } from '../features/pages/PagesView.tsx';
import { FinanceView } from '../features/finance/FinanceView.tsx';
import { HrView } from '../features/hr/HrView.tsx';
import { TicketsView } from '../features/tickets/TicketsView.tsx';
import { WorkspaceCalendarSection } from '../features/calendar/WorkspaceCalendarSection.tsx';
import { CustomSectionView, SectionsManagerSheet } from '../features/sections/CustomSections.tsx';
import { SocialInsightsView } from '../features/social/SocialInsights.tsx';
import { SocialContentView } from '../features/social/SocialContent.tsx';
import { SocialMarketingView } from '../features/social/SocialMarketing.tsx';
import { Avatar, EmptyState, PageLoader, WorkspaceAvatar } from '../ui/primitives.tsx';
import { useToast } from '../ui/Toast.tsx';
import { ApiError } from '../api/client.ts';

export function WorkspacePage() {
  const { wsKey } = useParams();
  const { orgId, base, openQuickAdd, me, detail } = useOrgCtx();
  const workspaces = useWorkspaces(orgId);
  const ws = workspaces.data?.find((w) => w.key === wsKey);
  const uploadLogo = useUploadWorkspaceLogo(orgId);
  const deleteLogo = useDeleteWorkspaceLogo(orgId);
  const toast = useToast();
  const logoInputRef = useRef<HTMLInputElement>(null);
  // Fixed module tabs, plus one dynamic tab per custom section (its id is its tab value).
  type Tab = 'tasks' | 'ped' | 'crm' | 'finance' | 'hr' | 'tickets' | 'documents' | 'people' | 'insights' | 'content' | 'marketing' | string;
  const [tab, setTab] = useState<Tab>(() => new URLSearchParams(window.location.search).get('tab') || 'tasks');
  const [managingSections, setManagingSections] = useState(false);
  const [managingOwner, setManagingOwner] = useState(false);
  const sections = useSections(orgId, ws?.id ?? '');
  const isOwnerOrAdmin = Boolean(detail?.capabilities.admin) || ws?.createdBy === me.user.id;

  if (workspaces.isLoading) return <PageLoader />;
  if (!ws) {
    return (
      <div className="page">
        <PageHeader title="Spazio" back={`${base}/menu`} />
        <div className="card"><EmptyState tone="neutral" title="Spazio non trovato">Non esiste o non ne fai parte.</EmptyState></div>
      </div>
    );
  }

  return (
    <div className="page wide" key={ws.id}>
      <PageHeader
        title={ws.name}
        back={`${base}/menu`}
        actions={<button type="button" className="btn btn-primary btn-sm" onClick={() => openQuickAdd({ workspaceId: ws.id })}><Plus size={16} /> Task</button>}
        mobileActions={<button type="button" className="icon-btn" aria-label="Nuova task" onClick={() => openQuickAdd({ workspaceId: ws.id })}><Plus size={20} /></button>}
        leading={
          <div className="ws-hero">
            {ws.canManage ? (
              <div className="avatar-upload">
                <WorkspaceAvatar name={ws.name} keyLabel={ws.key} color={ws.color} logoUrl={ws.logoUrl} active={ws.myAttentionTasks > 0} size={52} />
                <button type="button" className="avatar-upload-btn" aria-label="Cambia il logo dello spazio" disabled={uploadLogo.isPending} onClick={() => logoInputRef.current?.click()}>
                  <Camera size={13} />
                </button>
                <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  uploadLogo.mutate({ workspaceId: ws.id, file }, {
                    onSuccess: () => toast('Logo aggiornato'),
                    onError: (err) => toast(err instanceof ApiError ? err.message : 'Caricamento non riuscito'),
                  });
                }} />
              </div>
            ) : (
              <WorkspaceAvatar name={ws.name} keyLabel={ws.key} color={ws.color} logoUrl={ws.logoUrl} active={ws.myAttentionTasks > 0} size={52} />
            )}
            <p className="ws-hero-meta">
              <span>{WORKSPACE_TYPE_LABELS[ws.type]} · {ws.key} · {ws.openTasks} task aperte{ws.myOpenTasks ? ` · ${ws.myOpenTasks} tue` : ''}</span>
              {ws.canManage && ws.logoUrl && (
                <button type="button" className="btn btn-plain btn-sm ws-hero-link" disabled={deleteLogo.isPending}
                  onClick={() => deleteLogo.mutate(ws.id, { onSuccess: () => toast('Logo rimosso') })}>
                  <X size={13} /> Rimuovi logo
                </button>
              )}
            </p>
          </div>
        }
      />
      <div className="chips module-tabs" role="tablist" aria-label="Sezione">
        {([
          { value: 'tasks', label: 'Task' },
          ...(ws.pedEnabled || ws.canManage ? [{ value: 'ped' as const, label: 'Calendario', module: 'ped' as HidableModule }] : []),
          ...(ws.crmEnabled || ws.canManage ? [{ value: 'crm' as const, label: 'CRM', module: 'crm' as HidableModule }] : []),
          ...(ws.financeEnabled || ws.canManage ? [{ value: 'finance' as const, label: 'Contabilità', module: 'finance' as HidableModule }] : []),
          ...(ws.hrEnabled || ws.canManage ? [{ value: 'hr' as const, label: 'HR', module: 'hr' as HidableModule }] : []),
          ...(ws.ticketsEnabled || ws.canManage ? [{ value: 'tickets' as const, label: 'Ticket', module: 'tickets' as HidableModule }] : []),
          { value: 'documents', label: 'Documenti', module: 'documents' as HidableModule },
          { value: 'people', label: 'Persone', module: 'people' as HidableModule },
          { value: 'content', label: 'Contenuti', module: 'content' as HidableModule },
          { value: 'insights', label: 'Insights', module: 'insights' as HidableModule },
          { value: 'marketing', label: 'Marketing/ADV', module: 'marketing' as HidableModule },
          ...(sections.data ?? []).map((s) => ({ value: s.id, label: `${s.icon} ${s.name}` })),
        ] as { value: Tab; label: string; module?: HidableModule }[])
          .filter((t) => !t.module || (!ws.hiddenModules.includes(t.module) && !ws.myHiddenModules.includes(t.module)))
          .map((t) => t.value === 'crm' ? (
            <Link key={t.value} to={`${base}/w/${ws.key}/crm`} role="tab" className="chip" aria-label="Apri il CRM">{t.label}</Link>
          ) : (
            <button key={t.value} type="button" role="tab" className="chip" aria-selected={tab === t.value} aria-pressed={tab === t.value} onClick={() => setTab(t.value)}>
              {t.label}
            </button>
          ))}
        {ws.canManage && (
          <button type="button" className="chip" aria-label="Gestisci sezioni personalizzate" onClick={() => setManagingSections(true)}>
            <Settings2 size={14} />
          </button>
        )}
        {isOwnerOrAdmin && (
          <button type="button" className="chip" aria-label="Visibilità sezioni e proprietà spazio" onClick={() => setManagingOwner(true)}>
            <Shield size={14} />
          </button>
        )}
      </div>
      {tab === 'tasks' && <TaskExplorer workspaceId={ws.id} defaultScope="all" />}
      {tab === 'ped' && <PedView workspace={ws} />}
      {tab === 'finance' && <FinanceView workspace={ws} />}
      {tab === 'hr' && <HrView workspace={ws} />}
      {tab === 'tickets' && <TicketsView workspace={ws} />}
      {tab === 'documents' && <PagesView workspace={ws} />}
      {tab === 'people' && <WorkspacePeople workspaceId={ws.id} workspaceName={ws.name} canManage={ws.canManage} canDelete={ws.canDelete} />}
      {tab === 'content' && <SocialContentView workspaceId={ws.id} canManage={ws.canManage} />}
      {tab === 'insights' && <SocialInsightsView workspaceId={ws.id} canManage={ws.canManage} />}
      {tab === 'marketing' && <SocialMarketingView workspaceId={ws.id} canManage={ws.canManage} />}
      {sections.data?.filter((s) => s.id === tab).map((s) => <CustomSectionView key={s.id} workspaceId={ws.id} section={s} canManage={ws.canManage} />)}
      <SectionsManagerSheet open={managingSections} onClose={() => setManagingSections(false)} workspaceId={ws.id} sections={sections.data ?? []} />
      <WorkspaceOwnerSheet open={managingOwner} onClose={() => setManagingOwner(false)} ws={ws} />
    </div>
  );
}

const MODULE_LABELS: Record<HidableModule, string> = {
  ped: 'Calendario', crm: 'CRM', finance: 'Contabilità', hr: 'HR', tickets: 'Ticket', documents: 'Documenti', people: 'Persone',
  content: 'Contenuti', insights: 'Insights', marketing: 'Marketing/ADV',
};

/** Owner/admin-only: hide or re-show a workspace's built-in tabs (without touching the underlying module or
 * its data), and hand the workspace off to another member entirely. */
function WorkspaceOwnerSheet({ open, onClose, ws }: { open: boolean; onClose: () => void; ws: Workspace }) {
  const { orgId, base, me } = useOrgCtx();
  const navigate = useNavigate();
  const setVisibility = useSetModuleVisibility(orgId);
  const transfer = useTransferWorkspace(orgId);
  const members = useMembers(orgId, open);
  const toast = useToast();
  const [transferTo, setTransferTo] = useState<string | null>(null);

  // Every hidable module is listed here regardless of whether it's been activated yet (ped/crm/finance/hr/
  // tickets have their own separate on/off switch, reached by showing their tab and opening it): visibility
  // and activation are independent, so a module can be hidden before it's ever turned on.
  const candidates = (members.data ?? []).filter((m) => m.userId !== ws.createdBy && m.status === 'active');

  return (
    <Sheet open={open} onClose={onClose} title="Visibilità e proprietà" variant="center" labelledBy="owner-tools-title">
      <h3 className="subhead" style={{ marginTop: 0 }}>Sezioni visibili</h3>
      <p className="hint" style={{ marginTop: 0 }}>Nascondi le sezioni che non usi in questo spazio: restano attive con i loro dati, solo non visibili agli altri.</p>
      <div className="card list" style={{ marginBottom: 20 }}>
        {HIDABLE_MODULES.map((m) => {
          const hidden = ws.hiddenModules.includes(m);
          return (
            <div className="list-row" key={m}>
              <span className="grow">{MODULE_LABELS[m]}</span>
              <button
                type="button" className="btn btn-secondary btn-sm"
                onClick={() => setVisibility.mutate({ workspaceId: ws.id, module: m, hidden: !hidden }, { onError: () => toast('Operazione non riuscita') })}
              >
                {hidden ? <><Eye size={14} /> Mostra</> : <><EyeOff size={14} /> Nascondi</>}
              </button>
            </div>
          );
        })}
      </div>

      <h3 className="subhead">Cedi lo spazio</h3>
      <p className="hint" style={{ marginTop: 0 }}>Rendi un altro membro il proprietario: potrà eliminare lo spazio, cederlo a sua volta e gestirne la visibilità.</p>
      {transferTo ? (
        <div className="card card-pad form">
          <p style={{ margin: 0 }}>Cedere <strong>{ws.name}</strong> a <strong>{candidates.find((c) => c.userId === transferTo)?.displayName}</strong>? Non potrai più gestirlo come proprietario.</p>
          <div className="quick-row">
            <button type="button" className="btn btn-secondary" onClick={() => setTransferTo(null)}>Annulla</button>
            <button
              type="button" className="btn btn-danger" disabled={transfer.isPending}
              onClick={() => transfer.mutate({ workspaceId: ws.id, userId: transferTo }, {
                onSuccess: () => { toast('Spazio ceduto'); onClose(); if (ws.createdBy === me.user.id) navigate(`${base}/menu`); },
                onError: () => toast('Cessione non riuscita'),
              })}
            >
              <Repeat size={16} /> Conferma cessione
            </button>
          </div>
        </div>
      ) : (
        <div className="card list">
          {candidates.length === 0 && <p className="hint" style={{ padding: 16, margin: 0 }}>Nessun altro membro dell’organizzazione disponibile.</p>}
          {candidates.map((m) => (
            <button key={m.userId} type="button" className="list-row" onClick={() => setTransferTo(m.userId)}>
              <span className="grow">{m.displayName}<div className="secondary">{ROLE_LABELS[m.role]}</div></span>
              <Repeat size={16} color="var(--label-3)" />
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}

function WorkspacePeople({ workspaceId, workspaceName, canManage, canDelete }: { workspaceId: string; workspaceName: string; canManage: boolean; canDelete: boolean }) {
  const { orgId, base } = useOrgCtx();
  const navigate = useNavigate();
  const ws = useWorkspace(orgId, workspaceId);
  const orgMembers = useMembers(orgId, canManage);
  const mutate = useWorkspaceMember(orgId, workspaceId);
  const deleteWorkspace = useDeleteWorkspace(orgId);
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [editing, setEditing] = useState<WorkspaceMember | null>(null);
  if (!ws.data) return <PageLoader />;
  const inWs = new Set(ws.data.members.map((m) => m.userId));
  const candidates = (orgMembers.data ?? []).filter((m) => !inWs.has(m.userId) && m.status === 'active');

  return (
    <>
      <div className="card list">
        {ws.data.members.map((m) => (
          <div key={m.userId} className="list-row">
            <Avatar id={m.userId} name={m.displayName} avatarUrl={m.avatarUrl} size={36} />
            <span className="grow">
              <div>{m.displayName}</div>
              <div className="secondary">{m.position ? `${m.position} · ` : ''}{ROLE_LABELS[m.role]} · {m.email}</div>
            </span>
            {canManage && (
              <button type="button" className="icon-btn muted" aria-label={`Impostazioni di ${m.displayName} in questo spazio`} onClick={() => setEditing(m)}>
                <Settings2 size={17} />
              </button>
            )}
            {canManage && (
              <button type="button" className="icon-btn muted" aria-label={`Rimuovi ${m.displayName} dallo spazio`}
                onClick={() => mutate.mutate({ userId: m.userId, remove: true }, { onSuccess: () => toast(`${m.displayName} rimosso dallo spazio`) })}>
                <X size={18} />
              </button>
            )}
          </div>
        ))}
      </div>
      <MemberSettingsSheet open={Boolean(editing)} onClose={() => setEditing(null)} workspaceId={workspaceId} member={editing} />
      {canManage && (
        <section className="section" style={{ marginTop: 20 }}>
          {!adding ? (
            <button type="button" className="btn btn-secondary" onClick={() => setAdding(true)}><UserPlus size={17} /> Aggiungi persone</button>
          ) : (
            <>
              <h3 className="subhead">Membri dell’organizzazione</h3>
              <div className="card list">
                {candidates.length === 0 && <p className="hint" style={{ padding: 16, margin: 0 }}>Tutti i membri fanno già parte di questo spazio. Invita nuove persone da Impostazioni → Membri.</p>}
                {candidates.map((m) => (
                  <button key={m.userId} type="button" className="list-row" onClick={() => mutate.mutate({ userId: m.userId }, { onSuccess: () => toast(`${m.displayName} aggiunto`) })}>
                    <Avatar id={m.userId} name={m.displayName} avatarUrl={m.avatarUrl} size={32} />
                    <span className="grow">{m.displayName}<div className="secondary">{ROLE_LABELS[m.role]}</div></span>
                    <Plus size={18} color="var(--accent)" />
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      )}
      <WorkspaceCalendarSection workspaceId={workspaceId} canManage={canManage} />
      {canDelete && (
        <section className="section" style={{ marginTop: 20 }}>
          <h3 className="subhead danger">Zona pericolosa</h3>
          {!deleting ? (
            <button type="button" className="btn btn-danger" onClick={() => setDeleting(true)}><Trash2 size={17} /> Elimina spazio</button>
          ) : (
            <div className="card card-pad form">
              <p style={{ margin: 0 }}>
                Questo eliminerà <strong>{workspaceName}</strong> e tutto il suo contenuto (task, documenti e i moduli attivi) in modo permanente. Non si può annullare.
              </p>
              <label className="field">
                <span>Scrivi <strong>{workspaceName}</strong> per confermare</span>
                <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete="off" />
              </label>
              <div className="quick-row">
                <button type="button" className="btn btn-secondary" onClick={() => { setDeleting(false); setConfirmText(''); }}>Annulla</button>
                <button type="button" className="btn btn-danger" disabled={confirmText !== workspaceName || deleteWorkspace.isPending}
                  onClick={() => deleteWorkspace.mutate(workspaceId, {
                    onSuccess: () => { toast(`${workspaceName} eliminato`); navigate(`${base}/menu`); },
                    onError: () => toast('Non è stato possibile eliminare lo spazio'),
                  })}>
                  Elimina definitivamente
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}

/** Owner/manager-only: a custom display title (no permission tied to it) and which of this workspace's
 * tabs show up for this one person - independent of the workspace-wide visibility in WorkspaceOwnerSheet. */
function MemberSettingsSheet({ open, onClose, workspaceId, member }: { open: boolean; onClose: () => void; workspaceId: string; member: WorkspaceMember | null }) {
  const { orgId } = useOrgCtx();
  const update = useUpdateWorkspaceMemberSettings(orgId, workspaceId);
  const toast = useToast();
  const [position, setPosition] = useState('');
  const [hidden, setHidden] = useState<HidableModule[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  if (member && loadedFor !== member.userId) {
    setPosition(member.position ?? '');
    setHidden(member.hiddenModules);
    setLoadedFor(member.userId);
  }

  return (
    <Sheet open={open} onClose={onClose} title={member ? `${member.displayName} in questo spazio` : ''} variant="center" labelledBy="member-settings-title">
      {member && (
        <>
          <label className="field">
            <span>Posizione</span>
            <input className="input" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="es. Copywriter, Social Media Manager…" maxLength={60} />
          </label>
          <p className="hint">Un'etichetta libera, visibile solo qui in Persone: non cambia cosa {member.displayName.split(' ')[0]} può fare, solo come compare.</p>
          <h3 className="subhead">Sezioni visibili a {member.displayName.split(' ')[0]}</h3>
          <div className="card list" style={{ marginBottom: 20 }}>
            {HIDABLE_MODULES.map((m) => (
              <div className="list-row" key={m}>
                <span className="grow">{MODULE_LABELS[m]}</span>
                <button
                  type="button" role="switch" className="switch" aria-checked={!hidden.includes(m)} aria-label={MODULE_LABELS[m]}
                  onClick={() => setHidden((h) => (h.includes(m) ? h.filter((x) => x !== m) : [...h, m]))}
                />
              </div>
            ))}
          </div>
          <button
            type="button" className="btn btn-primary btn-block" disabled={update.isPending}
            onClick={() => update.mutate({ userId: member.userId, position: position.trim() || null, hiddenModules: hidden }, {
              onSuccess: () => { toast('Impostazioni aggiornate'); onClose(); },
              onError: () => toast('Salvataggio non riuscito'),
            })}
          >
            Salva
          </button>
        </>
      )}
    </Sheet>
  );
}
